import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js';
import toast from 'react-hot-toast';

//const SUBS_URL = 'http://localhost:5002/api';
const SUBS_URL = process.env.REACT_APP_SUBS_URL || 'http://localhost:5002/api';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||  
'pk_test_51TXYnFJR2JSc4G2bXUjEhnyhMkU9i52FWnZKZGyarNtkt78iGVCxXhLkJfDlUPJaidQJLssKAVf35EbGoLXzGaof00kwViwEfh';


let stripePromise: Promise<Stripe | null>;
const getStripe = () => {
    if (!stripePromise) stripePromise = loadStripe(STRIPE_PK);
    return stripePromise;
};

interface Plan {
    id: number;
    name: string;
    description: string;
    price: number;
    searchLimit: number;
    isFree: boolean;
    features: string[];
    badgeText?: string;
    color: string;
}

interface PlansModalProps {
    isOpen: boolean;
    onClose: () => void;
    reason?: string;
    message?: string;
}

const COLOR_CONFIG: Record<string, { card: string; btn: string; badge: string; check: string; price: string }> = {
    gray:   { card: 'border-gray-200', btn: 'bg-gray-700 hover:bg-gray-800', badge: 'bg-gray-100 text-gray-600', check: 'text-gray-500', price: 'text-gray-900' },
    teal:   { card: 'border-teal-400 ring-2 ring-teal-100 shadow-md', btn: 'bg-teal-600 hover:bg-teal-700', badge: 'bg-teal-500 text-white', check: 'text-teal-500', price: 'text-teal-700' },
    purple: { card: 'border-purple-300', btn: 'bg-purple-600 hover:bg-purple-700', badge: 'bg-purple-600 text-white', check: 'text-purple-500', price: 'text-purple-700' },
};

const PlansModal = ({ isOpen, onClose, reason, message }: PlansModalProps) => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState<'plans' | 'payment'>('plans');
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [paying, setPaying] = useState(false);
    const [cardError, setCardError] = useState('');
    const cardRef = useRef<StripeCardElement | null>(null);
    const cardDivRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        setStep('plans');
        setSelectedPlan(null);
        setCardError('');
        setLoading(true);
        const token = localStorage.getItem('sub_token');
        fetch(`${SUBS_URL}/plans`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
            .then(r => r.json())
            .then(setPlans)
            .catch(() => toast.error('Could not load plans'))
            .finally(() => setLoading(false));
    }, [isOpen]);

    // Mount Stripe Card Element when payment step opens
    useEffect(() => {
        if (step !== 'payment' || !cardDivRef.current) return;
        let card: StripeCardElement;

        getStripe().then(stripe => {
            if (!stripe || !cardDivRef.current) return;
            const elements = stripe.elements();
            card = elements.create('card', {
                style: {
                    base: {
                        fontSize: '14px',
                        color: '#1f2937',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        '::placeholder': { color: '#9ca3af' },
                    },
                    invalid: { color: '#ef4444' },
                },
                hidePostalCode: true,
            });
            card.mount(cardDivRef.current);
            card.on('change', e => setCardError(e.error?.message || ''));
            cardRef.current = card;
        });

        return () => { card?.destroy(); cardRef.current = null; };
    }, [step]);

    if (!isOpen) return null;

    const handleSelectPlan = (plan: Plan) => {
        if (!localStorage.getItem('sub_token')) { navigate('/login'); onClose(); return; }
        if (plan.isFree) { toast('You are already on the Basic plan!'); onClose(); return; }
        setSelectedPlan(plan);
        setStep('payment');
    };

    const handlePay = async () => {
        if (!selectedPlan || !cardRef.current) return;
        setPaying(true);
        setCardError('');

        try {
            const token = localStorage.getItem('sub_token');
            const stripe = await getStripe();
            if (!stripe) { toast.error('Stripe failed to load'); return; }

            // Step 1: Create PaymentIntent on our backend
            const intentRes = await fetch(`${SUBS_URL}/payment/create-intent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ planId: selectedPlan.id })
            });
            const intentData = await intentRes.json();

            if (!intentData.success || !intentData.clientSecret) {
                toast.error(intentData.message || 'Could not initiate payment');
                return;
            }

            // Step 2: Stripe.js confirms payment directly with Stripe servers
            // Card data never touches our backend
            const { error, paymentIntent } = await stripe.confirmCardPayment(
                intentData.clientSecret,
                { payment_method: { card: cardRef.current } }
            );

            if (error) {
                setCardError(error.message || 'Payment failed');
                return;
            }

            if (paymentIntent?.status !== 'succeeded') {
                toast.error('Payment was not completed');
                return;
            }

            // Step 3: Tell our backend to activate the plan
            const confirmRes = await fetch(`${SUBS_URL}/payment/confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    paymentIntentId: paymentIntent.id,
                    planId: selectedPlan.id
                })
            });
            const confirmData = await confirmRes.json();

            if (confirmData.success) {
                toast.success(`🎉 ${selectedPlan.name} plan activated!`);
                onClose();
                setTimeout(() => window.location.reload(), 800);
            } else {
                toast.error(confirmData.message || 'Activation failed');
            }
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setPaying(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">

                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
                    <div>
                        {message && (
                            <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3">
                                <svg className="text-amber-500 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                                </svg>
                                <p className="text-amber-700 text-xs font-semibold">{message}</p>
                            </div>
                        )}
                        <h2 className="text-xl font-bold text-gray-900">
                            {step === 'plans' ? '🚀 Choose Your Plan' : `Complete Payment — ${selectedPlan?.name}`}
                        </h2>
                        <p className="text-sm text-gray-400 mt-0.5">
                            {step === 'plans' ? 'Full access to 710,000+ medical codes' : 'Secure payment via Stripe'}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-xl transition-all flex-shrink-0">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M18 6 6 18M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    {/* Plans step */}
                    {step === 'plans' && (
                        loading ? (
                            <div className="grid grid-cols-3 gap-4">
                                {[1,2,3].map(i => (
                                    <div key={i} className="border border-gray-100 rounded-2xl p-5 animate-pulse space-y-3">
                                        <div className="h-5 bg-gray-100 rounded w-1/2"/>
                                        <div className="h-8 bg-gray-100 rounded w-3/4"/>
                                        {[1,2,3].map(j => <div key={j} className="h-3 bg-gray-50 rounded"/>)}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {plans.map(plan => {
                                    const c = COLOR_CONFIG[plan.color] || COLOR_CONFIG.teal;
                                    return (
                                        <div key={plan.id} className={`relative border-2 rounded-2xl p-5 flex flex-col transition-all ${c.card}`}>
                                            {plan.badgeText && (
                                                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3 py-1 rounded-full ${c.badge}`}>
                                                    {plan.badgeText}
                                                </span>
                                            )}
                                            <div className="mb-3">
                                                <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                                                <p className="text-xs text-gray-400 mt-0.5">{plan.description}</p>
                                            </div>
                                            <div className="mb-4">
                                                <div className="flex items-baseline gap-1">
                                                    <span className={`text-3xl font-black ${c.price}`}>
                                                        {plan.isFree ? 'Free' : `$${plan.price}`}
                                                    </span>
                                                    {!plan.isFree && <span className="text-sm text-gray-400">/mo</span>}
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {plan.searchLimit === -1 ? 'Unlimited searches' : `${plan.searchLimit.toLocaleString()} searches${plan.isFree ? '/day' : '/month'}`}
                                                </p>
                                            </div>
                                            <ul className="space-y-2 mb-5 flex-1">
                                                {plan.features.map((f, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <svg className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${c.check}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                                            <polyline points="20 6 9 17 4 12"/>
                                                        </svg>
                                                        <span className="text-[11px] text-gray-600">{f}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <button onClick={() => handleSelectPlan(plan)}
                                                className={`w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all ${c.btn}`}>
                                                {plan.isFree ? 'Current Plan' : `Get ${plan.name}`}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}

                    {/* Payment step */}
                    {step === 'payment' && selectedPlan && (
                        <div className="max-w-sm mx-auto space-y-5">
                            {/* Summary */}
                            <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-bold text-teal-800">{selectedPlan.name} Plan</p>
                                    <p className="text-xs text-teal-600 mt-0.5">{selectedPlan.searchLimit.toLocaleString()} searches · 30 days</p>
                                </div>
                                <span className="text-2xl font-black text-teal-700">${selectedPlan.price}</span>
                            </div>

                            {/* Stripe Card Element */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Card Details</label>
                                <div
                                    ref={cardDivRef}
                                    className={`w-full px-4 py-3.5 border rounded-xl bg-gray-50 focus-within:bg-white focus-within:border-teal-400 transition-all ${
                                        cardError ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                    }`}
                                />
                                {cardError && <p className="text-red-500 text-xs mt-1.5">{cardError}</p>}
                            </div>

                            {/* Test card hint */}
                            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5">
                                <svg className="text-blue-400 flex-shrink-0 mt-0.5" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                <div>
                                    <p className="text-blue-700 text-[11px] font-semibold">Test Card</p>
                                    <p className="text-blue-600 text-[10px] font-mono mt-0.5">4242 4242 4242 4242 · 12/28 · 123</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                                Secured by Stripe. Card data never touches our servers.
                            </div>

                            <button onClick={handlePay} disabled={paying}
                                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm text-sm">
                                {paying ? (
                                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Processing...</>
                                ) : `Pay $${selectedPlan.price} — Activate Now`}
                            </button>

                            <button onClick={() => setStep('plans')} className="w-full text-sm text-gray-400 hover:text-gray-600 py-1 transition-colors">
                                ← Back to plans
                            </button>
                        </div>
                    )}
                </div>

                {step === 'plans' && (
                    <div className="px-6 py-4 border-t border-gray-50 text-center">
                        <p className="text-xs text-gray-400">All paid plans · 30-day validity · Secured by Stripe</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlansModal;